import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

type LocationSnapshot = {
  path: string;
  hash: string;
};

function useLocationSnapshot(pathname: string) {
  const [location, setLocation] = useState<LocationSnapshot>(() => ({
    path: pathname,
    hash: '',
  }));

  useEffect(() => {
    const syncLocation = () =>
      setLocation({
        path: window.location.pathname,
        hash: window.location.hash,
      });

    syncLocation();
    window.addEventListener('hashchange', syncLocation);
    window.addEventListener('popstate', syncLocation);
    return () => {
      window.removeEventListener('hashchange', syncLocation);
      window.removeEventListener('popstate', syncLocation);
    };
  }, []);

  return location;
}

function StudioHeader({ pathname }: { pathname: string }) {
  const location = useLocationSnapshot(pathname);

  return (
    <header className="site-header shell">
      <a
        className="wordmark studio-wordmark"
        href="/"
        aria-label="alxnsv.io home"
      >
        <img
          className="studio-mark"
          src="/brand/alxnsv-mark.svg"
          width="64"
          height="64"
          alt=""
        />
        <span>alxnsv.io</span>
      </a>

      <nav className="primary-nav" aria-label="Primary navigation">
        <a
          href="/#projects"
          aria-current={
            location.path === '/' && location.hash === '#projects'
              ? 'location'
              : undefined
          }
        >
          Projects
        </a>
        <a
          href="/#principles"
          aria-current={
            location.path === '/' && location.hash === '#principles'
              ? 'location'
              : undefined
          }
        >
          Principles
        </a>
        <a
          href="/#about"
          aria-current={
            location.path === '/' && location.hash === '#about'
              ? 'location'
              : undefined
          }
        >
          About
        </a>
      </nav>

      <a className="header-contact" href="mailto:hello@alxnsv.io">
        Say hello
        <ArrowUpRight size={15} strokeWidth={2} aria-hidden="true" />
      </a>
    </header>
  );
}

function StudioFooter() {
  return (
    <footer className="site-footer">
      <div className="shell footer-inner">
        <div>
          <a
            className="wordmark wordmark-footer"
            href="/"
            aria-label="alxnsv.io home"
          >
            <img
              className="studio-mark studio-mark-footer"
              src="/brand/alxnsv-mark.svg"
              width="64"
              height="64"
              alt=""
            />
            <span>alxnsv.io</span>
          </a>
          <p>Independent software, built patiently.</p>
        </div>
        <div className="footer-links">
          <a href="/#projects">Projects</a>
          <a href="/#principles">Principles</a>
          <a href="/#about">About</a>
          <a href="mailto:hello@alxnsv.io">hello@alxnsv.io</a>
        </div>
        <p className="copyright">© {new Date().getFullYear()} alxnsv.io</p>
      </div>
    </footer>
  );
}

const spoolmarkNavigation = [
  { label: 'Product', href: '/spoolmark#features' },
  { label: 'Compatibility', href: '/spoolmark#compatibility' },
  { label: 'Journal', href: '/spoolmark/blog' },
  { label: 'Docs', href: '/spoolmark/docs' },
] as const;

const spoolmarkDocumentPaths = new Set([
  '/spoolmark/docs',
  '/spoolmark/privacy',
  '/spoolmark/terms',
  '/spoolmark/support',
  '/spoolmark/data-deletion',
]);

function SpoolmarkHeader({ pathname }: { pathname: string }) {
  const location = useLocationSnapshot(pathname);

  return (
    <header className="spoolmark-header shell">
      <div className="spoolmark-header-brand">
        <a
          className="spoolmark-studio-link"
          href="/"
          aria-label="Back to alxnsv.io"
        >
          <ArrowLeft size={13} aria-hidden="true" />
          alxnsv.io
        </a>
        <a
          className="spoolmark-wordmark"
          href="/spoolmark"
          aria-label="Spoolmark home"
        >
          <img
            src="/images/spoolmark/app-icon.png"
            width="36"
            height="36"
            alt=""
          />
          <span>Spoolmark</span>
        </a>
      </div>

      <nav
        className="primary-nav spoolmark-nav"
        aria-label="Spoolmark navigation"
      >
        {spoolmarkNavigation.map((item) => (
          <a
            href={item.href}
            key={item.href}
            aria-current={
              item.href === '/spoolmark/blog' &&
              (location.path.startsWith('/spoolmark/blog') ||
                location.path === '/spoolmark/journal')
                ? 'page'
                : item.href === '/spoolmark/docs' &&
                    spoolmarkDocumentPaths.has(location.path)
                  ? 'page'
                  : item.href.includes('#') &&
                      location.path === '/spoolmark' &&
                      location.hash === `#${item.href.split('#')[1]}`
                    ? 'location'
                    : undefined
            }
          >
            {item.label}
          </a>
        ))}
      </nav>

      <a
        className="header-contact spoolmark-header-cta"
        href="/spoolmark#availability"
        aria-current={
          location.path === '/spoolmark' && location.hash === '#availability'
            ? 'location'
            : undefined
        }
      >
        Get the app
        <ArrowRight size={15} strokeWidth={2} aria-hidden="true" />
      </a>
    </header>
  );
}

function SpoolmarkFooter() {
  return (
    <footer className="spoolmark-footer">
      <div className="shell spoolmark-footer-inner">
        <div className="spoolmark-footer-brand">
          <img
            src="/images/spoolmark/app-icon.png"
            width="48"
            height="48"
            alt=""
          />
          <div>
            <a href="/spoolmark">Spoolmark</a>
            <p>Know every spool.</p>
          </div>
        </div>

        <nav className="spoolmark-footer-links" aria-label="Spoolmark footer">
          <a href="/spoolmark">Product</a>
          <a href="/spoolmark/blog">Journal</a>
          <a href="/spoolmark/docs">Docs</a>
          <a href="/spoolmark/privacy">Privacy</a>
          <a href="/spoolmark/terms">Terms</a>
          <a href="/spoolmark/support">Support</a>
          <a href="/spoolmark/data-deletion">Data & deletion</a>
        </nav>

        <p className="spoolmark-footer-note">
          Built independently by <a href="/">Alex Nosov</a>. Spoolmark is not
          affiliated with Bambu Lab or ACS.
        </p>
        <p className="spoolmark-copyright">
          © {new Date().getFullYear()} Spoolmark
        </p>
      </div>
    </footer>
  );
}

function SkipLink() {
  return (
    <a className="skip-link" href="#content">
      Skip to content
    </a>
  );
}

export function StudioShell({
  children,
  pathname = '/',
}: {
  children: ReactNode;
  pathname?: string;
}) {
  return (
    <div className="studio-site">
      <SkipLink />
      <StudioHeader pathname={pathname} />
      {children}
      <StudioFooter />
    </div>
  );
}

export function SpoolmarkShell({
  children,
  pathname = '/spoolmark',
}: {
  children: ReactNode;
  pathname?: string;
}) {
  return (
    <div className="spoolmark-site">
      <SkipLink />
      <SpoolmarkHeader pathname={pathname} />
      {children}
      <SpoolmarkFooter />
    </div>
  );
}
